import { onClick } from "@nx-paeonia/utils-events";
import { isErr, ok, type Result } from "@nx-paeonia/utils-result";
import { captureMessage } from "@sentry/browser";
import { type AnimationSequence, animate, resize } from "motion";

type CollapsibleOn = "never" | "always" | "mobile" | "desktop";

resize((info) => {
	const elements = document.querySelectorAll<HTMLDetailsElement>(".footer-collapsible");

	for (const element of elements) {
		handleElementOnResize(element, info.width);
	}
});

function handleElementOnResize(element: HTMLDetailsElement, width: number): void {
	const collapsibleOn = getElementCollapsibleOn(element);

	if (isErr(collapsibleOn)) {
		captureMessage(`Invalid collapsibleOn value: ${collapsibleOn.error}`);
		return;
	}

	switch (collapsibleOn.data) {
		case "never":
			element.setAttribute("open", "");
			break;
		case "always":
			element.removeAttribute("open");
			break;
		case "mobile":
			if (width > 768) {
				element.setAttribute("open", "");
			} else {
				element.removeAttribute("open");
			}
			break;
		case "desktop":
			if (width <= 768) {
				element.setAttribute("open", "");
			} else {
				element.removeAttribute("open");
			}
			break;
	}

	if (collapsibleOn.data === "always") {
		element.removeAttribute("open");
	}

	if (shouldBeOpenOn(collapsibleOn.data)) {
		element.setAttribute("open", "");
	}
}

onClick(".footer-collapsible summary", (event, element) => {
	event.preventDefault();
	const collapsibleOn = getElementCollapsibleOn(element);

	if (isErr(collapsibleOn)) {
		console.error("Invalid collapsibleOn value:", collapsibleOn.error);
		return;
	}

	if (collapsibleOn.data === "never") {
		return;
	}

	const parent = element.closest("details");

	if (shouldCollapseOn("always", collapsibleOn.data)) {
		toggleParentCollapsible(parent);
		return;
	}

	if (shouldCollapseOn("mobile", collapsibleOn.data)) {
		toggleParentCollapsible(parent);
	}

	if (shouldCollapseOn("desktop", collapsibleOn.data)) {
		toggleParentCollapsible(parent);
	}
});

function toggleParentCollapsible(parent: HTMLDetailsElement | null): void {
	if (parent?.hasAttribute("open")) {
		closeCollapsible(parent);
	} else if (parent) {
		openCollapsible(parent);
	}
}

function shouldCollapseOn(checkFor: CollapsibleOn, collapsibleOn: CollapsibleOn): boolean {
	if (checkFor === "mobile" && collapsibleOn === "mobile") {
		return window.innerWidth <= 768;
	}

	if (checkFor === "desktop" && collapsibleOn === "desktop") {
		return window.innerWidth > 768;
	}

	if (checkFor === "always" && collapsibleOn === "always") {
		return true;
	}

	return false;
}

function shouldBeOpenOn(collapsibleOn: CollapsibleOn): boolean {
	if (collapsibleOn === "never") {
		return true;
	}

	if (collapsibleOn === "mobile") {
		return window.innerWidth > 768;
	}

	if (collapsibleOn === "desktop") {
		return window.innerWidth <= 768;
	}

	return false;
}

function getElementCollapsibleOn(element: HTMLElement): Result<CollapsibleOn, "never"> {
	const collapsibleOn = element.dataset.collapsibleOn;
	if (collapsibleOn === "never" || collapsibleOn === "always" || collapsibleOn === "mobile" || collapsibleOn === "desktop") {
		return ok(collapsibleOn as CollapsibleOn);
	}
	return ok("never");
}

function openCollapsible(element: HTMLDetailsElement): void {
	const animationSequence: AnimationSequence = [];

	const summary = element.querySelector("summary");
	if (summary) {
		animationSequence.push([summary, { "--collapsible-summary-after-scale": [1, 0] }]);
	}

	const body = element.querySelector(":scope > div");
	if (body) {
		animationSequence.push([
			body,
			{ height: [0, body.scrollHeight], opacity: [0, 0, 1], translateY: [10, 10, 0], translateX: [10, 10, 0], overflow: "hidden" },
		]);
	}

	const animation = animate(animationSequence);
	animation.cancel();
	element.setAttribute("open", "");
	animation.play();
}

function closeCollapsible(element: HTMLDetailsElement): void {
	const animationSequence: AnimationSequence = [];

	const body = element.querySelector(":scope > div");
	if (body) {
		animationSequence.push([
			body,
			{ height: [body.scrollHeight, 0], opacity: [1, 0], translateY: [0, 10], translateX: [0, 10], overflow: "hidden" },
		]);
	}

	const summary = element.querySelector("summary");
	if (summary) {
		animationSequence.push([summary, { "--collapsible-summary-after-scale": [0, 1] }]);
	}

	const animation = animate(animationSequence);

	animation.finished.then(() => {
		element.removeAttribute("open");
	});
}

globalThis.dispatchEvent(new Event("resize"));
